import React, { useState, useEffect, useRef, useReducer } from 'react';
import JobItem from './JobItem';

interface StaticJobsListProps {
  handleStaticJobClick: (jobKey: 'marketingDirector' | 'devOpsEngineer') => void;
  handleActionClick: (jobId?: number) => void;
  getActionButtonText: (jobId: number) => string;
  getActionButtonVariant: (jobId: number) => 'outline-primary' | 'outline-danger' | 'transparent';
  message?: string;
  associatingJobs?: Set<number>;
}

// Simple reducer that just increments the counter on each action
const forceUpdateReducer = (state: number, action: 'increment'): number => {
  return state + 1;
};

const StaticJobsList: React.FC<StaticJobsListProps> = ({
  handleStaticJobClick,
  handleActionClick,
  getActionButtonText,
  getActionButtonVariant,
  message,
  associatingJobs = new Set()
}) => {
  // Keep track of previously processing jobs
  const processingJobsRef = useRef<Set<number>>(new Set());
  
  // Force a re-render when a job's processing state changes
  const [forceUpdateCounter, dispatchForceUpdate] = useReducer(forceUpdateReducer, 0);
  
  useEffect(() => {
    // Compare current associatingJobs with previous state
    const currentProcessingJobs = new Set(associatingJobs);
    const previousProcessingJobs = new Set(processingJobsRef.current);
    
    // Check if any jobs changed state
    let stateChanged = false;
    
    // Check for newly processing jobs
    Array.from(currentProcessingJobs).forEach(jobId => {
      if (!previousProcessingJobs.has(jobId)) {
        stateChanged = true;
      }
    });
    
    // Check for jobs that finished processing
    Array.from(previousProcessingJobs).forEach(jobId => {
      if (!currentProcessingJobs.has(jobId)) {
        stateChanged = true;
      }
    });
    
    // Update ref for next comparison
    processingJobsRef.current = currentProcessingJobs;
    
    // Force re-render if state changed
    if (stateChanged) {
      dispatchForceUpdate('increment');
    }
  }, [associatingJobs]);
  
  return (
    <div>
      {message && <p className="text-center mb-3">{message}</p>}
      
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: '1rem',
        marginTop: '1rem',
        textAlign: 'left'
      }}>
        <JobItem
          key={`9999-${associatingJobs.has(9999) ? `processing-${forceUpdateCounter}` : 'idle'}`}
          title="Marketing Director"
          location="Stamford, Connecticut"
          isActive={true}
          jobId={9999} // Dummy ID for static demo job
          onJobClick={() => handleStaticJobClick('marketingDirector')}
          onActionClick={handleActionClick}
          actionButtonText={getActionButtonText(9999)}
          actionButtonVariant={getActionButtonVariant(9999)}
        />
        <JobItem
          key={`8888-${associatingJobs.has(8888) ? `processing-${forceUpdateCounter}` : 'idle'}`}
          title="DevOps Engineer"
          location="Stamford, Connecticut"
          isActive={true}
          jobId={8888} // Dummy ID for static demo job
          onJobClick={() => handleStaticJobClick('devOpsEngineer')}
          onActionClick={handleActionClick}
          actionButtonText={getActionButtonText(8888)}
          actionButtonVariant={getActionButtonVariant(8888)}
        />
      </div>
    </div>
  );
};

export default StaticJobsList; 